import { useEffect, useRef, useState, useCallback } from "react";

/*
  Frecuencias fundamentales aproximadas de las cuerdas de guitarra estándar
  (considerando armónicos inferiores también).
  Buscamos energía en una banda alrededor de cada fundamental.
*/
const STRING_RANGES = [
  { string: 6, label: "E", low: 70,  high: 95 },   // E2  ~82Hz
  { string: 5, label: "A", low: 100, high: 125 },  // A2  ~110Hz
  { string: 4, label: "D", low: 135, high: 160 },  // D3  ~147Hz
  { string: 3, label: "G", low: 180, high: 215 },  // G3  ~196Hz
  { string: 2, label: "B", low: 230, high: 270 },  // B3  ~247Hz
  { string: 1, label: "E", low: 300, high: 360 },  // E4  ~330Hz
];

export interface StringEnergy {
  string: number;
  label: string;
  value: number; // 0 - 1
}

export function useStringSpectrum(enabled: boolean) {
  const [energies, setEnergies] = useState<StringEnergy[]>(
    STRING_RANGES.map((r) => ({ string: r.string, label: r.label, value: 0 }))
  );
  const [volume, setVolume] = useState(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;
    } catch (err) {
      console.error("Mic access denied", err);
    }
  }, []);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    analyserRef.current = null;
    streamRef.current = null;
    setEnergies(STRING_RANGES.map((r) => ({ string: r.string, label: r.label, value: 0 })));
    setVolume(0);
  }, []);

  useEffect(() => {
    if (!enabled) {
      stop();
      return;
    }
    start();

    const bufferLength = 2048;
    const dataArray = new Uint8Array(bufferLength);
    const sampleRate = 44100; // aproximado, se actualiza al conectar

    const update = () => {
      const analyser = analyserRef.current;
      if (!analyser) {
        rafRef.current = requestAnimationFrame(update);
        return;
      }

      analyser.getByteFrequencyData(dataArray);
      const nyquist = analyser.context.sampleRate / 2;
      const binSize = nyquist / dataArray.length;

      let totalEnergy = 0;
      const newEnergies = STRING_RANGES.map((range) => {
        const lowBin = Math.floor(range.low / binSize);
        const highBin = Math.ceil(range.high / binSize);
        let sum = 0;
        let count = 0;
        for (let i = lowBin; i <= highBin && i < dataArray.length; i++) {
          sum += dataArray[i];
          count++;
        }
        const avg = count > 0 ? sum / count : 0;
        totalEnergy += avg;
        // Normalizar: 0-255 → 0-1, con umbral
        const normalized = Math.min(1, Math.max(0, (avg - 30) / 150));
        return { string: range.string, label: range.label, value: normalized };
      });

      setEnergies(newEnergies);
      setVolume(Math.min(1, totalEnergy / (6 * 255)));
      rafRef.current = requestAnimationFrame(update);
    };

    rafRef.current = requestAnimationFrame(update);
    return () => {
      cancelAnimationFrame(rafRef.current);
      stop();
    };
  }, [enabled, start, stop]);

  return { energies, volume };
}
